import type { Permission } from '../access/role';
import { STAGE_LABEL, type Stage } from './stage';
import type { KitSpecification } from './kit';

/**
 * Which bench owns which specification field. Section 1 of the request of
 * 13 September 2026.
 *
 * The ingot is chosen at Wax and the block is milled at CAD / CAM, so those are
 * the two benches that know what actually went into the case. Everything else
 * in the specification is reception's, from the dentist's slip.
 *
 * This is not an access-matrix question and deliberately adds no permission. It
 * is a fact about which bench knows which material, and a fact like that
 * belongs in a table two entries long rather than in a matrix the lab can edit:
 * granting somebody at Ceramic the right to record an ingot press would not
 * make them the person who opened the box.
 */
export const SPEC_FIELD_DEPARTMENT = {
  ingot: 'wax',
  block: 'cadcam',
} as const satisfies Partial<Record<keyof KitSpecification, Stage>>;

export type SpecField = keyof typeof SPEC_FIELD_DEPARTMENT;

export const SPEC_FIELDS = Object.keys(SPEC_FIELD_DEPARTMENT) as SpecField[];

export function isSpecField(value: string): value is SpecField {
  return (SPEC_FIELDS as string[]).includes(value);
}

/**
 * Whether this person may record this field.
 *
 * The department owns the field for the LIFE of the case, not only while the
 * tray is on its bench: a ceramist at the next stage calling back to say the
 * ingot was wrong is a normal Tuesday, and the person who fixes it is the one
 * at Wax who has the box in front of them.
 *
 * A holder of `editCase` may edit both, through the ordinary case edit of
 * handover 2.4 as well as here. Management does not stop being able to fix a
 * typo because a table says Wax owns the field.
 */
export function canEditSpecField(
  field: SpecField,
  user: { department: string },
  permissions: readonly Permission[],
): boolean {
  return permissions.includes('editCase') || user.department === SPEC_FIELD_DEPARTMENT[field];
}

/** What the field is called in a sentence. The screen's labels say the same. */
const SPEC_FIELD_NAME: Record<SpecField, string> = {
  ingot: 'The ingot press',
  block: 'The zircon block',
};

/**
 * The refusals, rendered verbatim by the frontend, and answered in the same
 * words by the API. The bench is named exactly as every other screen names it,
 * out of `STAGE_LABEL`, so the sentence cannot drift from the menu.
 */
export const SPEC_FIELD_REFUSAL = {
  notYours: (field: SpecField) =>
    `${SPEC_FIELD_NAME[field]} is recorded by ${STAGE_LABEL[SPEC_FIELD_DEPARTMENT[field]]}.`,
  /** Neither field sent: the field error on `ingot`, which is the first of the two. */
  nothingSent: 'Send the ingot press, the zircon block, or both',
  /** A case that is deleted or dispatched is off the floor and off the bench. */
  gone: 'This case has left the floor.',
} as const;

/** The longest either field may be. A material name, not a note. */
export const SPEC_FIELD_MAX = 60;
